import {
  dateKindOf,
  dayStringToLocal,
  isChoiceFieldType,
  isCustomFieldValueBlank,
  isDayString,
} from "@salestrak/custom-fields";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
  Badge,
  Box,
  Button,
  formatDay,
  Icon,
  type IconName,
  Separator,
  Text,
} from "@salestrak/ui";
import { Fragment, useMemo, useState, type ReactNode } from "react";

import { getCustomFieldDisplayValue } from "#app/components/custom-fields/custom-field-renderers";
import { getOptionMetas } from "#app/components/custom-fields/option-visuals";
import type { CustomFieldDef } from "#app/components/custom-fields/types";
import { EntityTag } from "#app/components/entity";
import { FormAnswerValue, formOptionProperty } from "#app/components/forms/form-property-options";
import { useMemberEntityResolver } from "#app/lib/data";
import {
  type Form,
  type FormSubmission,
  isFieldItem,
  isFillableItem,
  type FormItem,
  resolveItemField,
  stringIds,
  walkForm,
} from "#app/lib/forms";
import { toDateValue } from "#app/lib/custom-field-utils";
import { formatDateTime, getHourCycle } from "#app/lib/utils/format-date";

import { formPhotoDeleteMessage } from "./delete-submission";
import { FormFillCard, FormFillStack, FormSectionHeading } from "./form-fill-body";
import { FormPhotoGrid } from "./form-photos-answer";
import { SUBMISSION_STATUS_LABEL } from "./submission-status-badge";

type TrailEntry = { action: "created" | "edited"; at: string; by: string };

const TRAIL_EVENT: Record<TrailEntry["action"], { icon: IconName; verb: string }> = {
  created: { icon: "clock", verb: "Started" },
  edited: { icon: "pencil", verb: "Edited" },
};

// Rows stored before a submitted filling became final can carry an `edited` entry per save.
const TRAIL_VISIBLE_ROWS = 2;

/** A choice answer's option labels as one list sentence, in the options'
 *  own order; a value the field no longer offers reads as itself. */
const CHOICE_LIST = new Intl.ListFormat(undefined, { style: "long", type: "conjunction" });

function choiceSentence(field: CustomFieldDef, value: unknown): string {
  const picked = Array.isArray(value) ? value.map(String) : [];
  const options = getOptionMetas(field);
  const known = options.filter((option) => picked.includes(option.value)).map((o) => o.label);
  const stray = picked.filter((id) => !options.some((option) => option.value === id));
  return CHOICE_LIST.format([...known, ...stray]);
}

export function FormSubmissionRead({
  form,
  submission,
  customFieldsById,
  onDeletePhoto,
}: {
  form: Pick<Form, "items" | "name">;
  submission: FormSubmission;
  customFieldsById: ReadonlyMap<string, CustomFieldDef>;
  onDeletePhoto?: ((photoId: string) => void) | undefined;
}) {
  const [deleting, setDeleting] = useState<{ photoId: string; item: FormItem } | null>(null);
  const items = useMemo(
    () => readSheetItems(form, submission, customFieldsById),
    [customFieldsById, form, submission],
  );
  const blocks: ReactNode[] = [];
  let run: { key: string; rows: ReactNode[] } | null = null;
  const flush = () => {
    if (!run) return;
    const { key, rows } = run;
    blocks.push(
      <FormFillCard key={key}>
        <Box direction="column">
          {rows.map((row, index) => (
            <Fragment key={index}>
              {index > 0 ? <Separator /> : null}
              {row}
            </Fragment>
          ))}
        </Box>
      </FormFillCard>,
    );
    run = null;
  };
  for (const item of items) {
    if (item.kind === "section") {
      flush();
      blocks.push(
        <Box key={item.itemId} paddingTop="sm" paddingX="xs">
          <FormSectionHeading item={item} />
        </Box>,
      );
      continue;
    }
    if (item.kind === "text") {
      flush();
      blocks.push(
        <Box direction="column" gap="2xs" key={item.itemId} paddingX="xs">
          {item.label ? (
            <Text size="sm" weight="semibold">
              {item.label}
            </Text>
          ) : null}
          {item.body ? (
            <Text pre size="sm" variant="muted">
              {item.body}
            </Text>
          ) : null}
        </Box>,
      );
      continue;
    }
    if (!isFieldItem(item)) continue;
    const value = submission.answers[item.itemId];
    const blank = isCustomFieldValueBlank(value);
    const field = resolveItemField(item, customFieldsById);
    run ??= { key: item.itemId, rows: [] };
    run.rows.push(
      <Box
        direction="column"
        gap="2xs"
        key={item.itemId}
        paddingY="sm"
      >
        <Text size="xs" variant="muted">
          {item.label || field?.name}
        </Text>
        {!field ? (
          <Text size="sm" variant="faint">
            Field removed
          </Text>
        ) : blank ? (
          <Text size="sm" variant="faint">
            No answer
          </Text>
        ) : field.type === "photos" && Array.isArray(value) ? (
          <Box paddingTop="2xs">
            <FormPhotoGrid
              ids={stringIds(value)}
              deleted={submission.deletedPhotoIds ?? undefined}
              onDelete={onDeletePhoto ? (photoId) => setDeleting({ item, photoId }) : undefined}
              title={item.label || field.name}
            />
          </Box>
        ) : (
          <FormAnswerValue
            fallback={
              <Text pre size="sm" weight="medium">
                {isChoiceFieldType(field.type)
                  ? choiceSentence(field, value)
                  : (answerDate(field, value) ?? getCustomFieldDisplayValue(field, value))}
              </Text>
            }
            property={formOptionProperty(item)}
            value={value}
          />
        )}
      </Box>,
    );
  }
  flush();

  return (
    <FormFillStack>
      {blocks}
      <AlertDialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
      >
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete photo</AlertDialogTitle>
            <AlertDialogDescription>
              {deleting ? formPhotoDeleteMessage(form, submission) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogClose render={<Button variant="outline" />}>Cancel</AlertDialogClose>
            <Button
              color="destructive"
              onClick={() => {
                if (deleting) onDeletePhoto?.(deleting.photoId);
                setDeleting(null);
              }}
            >
              Delete
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </FormFillStack>
  );
}

// A submitted sheet reads the list pinned at Submit, so later form edits never reach it; only wording comes off the form.
function readSheetItems(
  form: Pick<Form, "items">,
  submission: FormSubmission,
  customFieldsById: ReadonlyMap<string, CustomFieldDef>,
): FormItem[] {
  let asked: FormItem[];
  if (submission.itemIds) {
    const byId = new Map(form.items.map((item) => [item.itemId, item]));
    asked = submission.itemIds
      .map((itemId) => byId.get(itemId))
      .filter((item): item is FormItem => item !== undefined);
  } else {
    asked = walkForm(form, submission.answers, customFieldsById).items;
  }
  return asked.filter(
    (item) =>
      !isFieldItem(item) ||
      !isCustomFieldValueBlank(submission.answers[item.itemId]) ||
      isFillableItem(item, customFieldsById),
  );
}

export function SubmissionStatement({ submission }: { submission: FormSubmission }) {
  const [expanded, setExpanded] = useState(false);
  const submitted = submission.status === "submitted";
  const events: TrailEntry[] = [
    ...(submission.updatedBy && !submitted
      ? [{ action: "edited" as const, at: submission.updatedAt, by: submission.updatedBy }]
      : []),
    { action: "created", at: submission.createdAt, by: submission.createdBy },
  ];
  const latest = events[0];
  const trail = submitted ? events : events.slice(1);
  const hidden = trail.length - TRAIL_VISIBLE_ROWS;
  const rows = expanded || hidden <= 0 ? trail : trail.slice(0, TRAIL_VISIBLE_ROWS);
  return (
    <Box direction="column" gap="xs">
      {submission.versionNumber !== null ? (
        <Box direction="row">
          <Badge tone="outline">Version {submission.versionNumber}</Badge>
        </Box>
      ) : null}
      <StatementLine
        by={
          submitted
            ? (submission.submittedBy ?? submission.createdBy)
            : (latest?.by ?? submission.createdBy)
        }
        icon={
          <Icon
            color={submitted ? "success" : "warning"}
            name={submitted ? "check-circle" : "clock"}
            size="sm"
          />
        }
        verb={
          <Text size="sm" variant={submitted ? "success" : "warning"} weight="medium">
            {SUBMISSION_STATUS_LABEL[submission.status]}
          </Text>
        }
        when={
          submitted
            ? (submission.submittedAt ?? submission.createdAt)
            : (latest?.at ?? submission.createdAt)
        }
      />
      {rows.map((entry, index) => (
        <StatementLine
          by={entry.by}
          icon={<Icon color="faint" name={TRAIL_EVENT[entry.action].icon} size="sm" />}
          key={`${entry.action}-${entry.at}-${index}`}
          verb={
            <Text size="sm" variant="muted">
              {TRAIL_EVENT[entry.action].verb}
            </Text>
          }
          when={entry.at}
        />
      ))}
      {hidden > 0 && !expanded ? (
        <Box>
          <Button onClick={() => setExpanded(true)} size="xs" variant="ghost">
            <Text size="xs" variant="muted">
              Show {hidden} more
            </Text>
          </Button>
        </Box>
      ) : null}
    </Box>
  );
}

function StatementLine({
  icon,
  verb,
  by,
  when,
}: {
  icon: ReactNode;
  verb: ReactNode;
  by: string;
  when: string;
}) {
  const memberEntity = useMemberEntityResolver();
  return (
    <Box align="center" direction="row" gap="xs" minWidth={0} wrap>
      {icon}
      {verb}
      <Text size="sm" variant="muted">
        by
      </Text>
      <EntityTag
        entity={memberEntity(by) ?? { id: by, kind: "user", user: null }}
        labelInk="muted"
        size="sm"
      />
      <Text size="sm" variant="faint">
        ·
      </Text>
      <Text size="sm" variant="muted">
        {formatDateTime(when)}
      </Text>
    </Box>
  );
}

function answerDate(field: CustomFieldDef, value: unknown): string | null {
  const kind = dateKindOf(field);
  if (kind !== "date" && kind !== "dateTime") return null;
  if (isDayString(value)) return formatDay(dayStringToLocal(value));
  const date = toDateValue(value);
  if (!date) return null;
  return formatDay(date, {
    hourCycle: getHourCycle(),
    time: kind === "dateTime",
  });
}
